import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { forkJoin, Observable, ObservableInput, of } from 'rxjs';
import { first, map } from "rxjs/operators";
import * as _ from 'lodash';
import { EndpointConfig } from "src/app/config/endpoint-config";
import { ResizeOption, UdpImageResponse } from "src/app/model/web-data";
import { NGXLogger } from "ngx-logger";
import { TemplateDataset } from "src/app/model/template/template";
import { Dataset } from "src/app/model/dataset/dataset";
import { CommonUtils } from "src/app/utils/common";
import { ImageRequest } from "src/app/model/imageRequest/imageRequest";


@Injectable()
export class WebDataApiService {

  MAX_CHUNK_SIZE = 100

  constructor(
    private http: HttpClient,
    private endpointConfig: EndpointConfig,
    private logger: NGXLogger,
  ) {

  }

  // ------------------------
  // Request API section
  // ------------------------

  /**
   * UDP Image取得APIのURIからFire Storeのコレクションパスを取得する。
   * @param {string}  udpImageUri - (e.g.) 'https://stg-tky-apigw-https-int-dlkudp.fastretailing.com/V1/fr-stg01-dlk-um-store_survey/FRJP/images/008753f99a644db89517cc5b33f45538'
   * @return {string} (e.g.) 'fr-stg01-dlk-um-store_survey/FRJP/images'
  */
  public getUDPImageCollectionPath(udpImageUri: string) {
    // host部分とdocumentId部分を除き、コレクション部分のみのパスを生成する。
    let regExpMatch = udpImageUri.match(/V1\/(.*\/images)/)
    let collectionPath = regExpMatch
      ? regExpMatch[1]
      : ""
    return collectionPath
  }

  public getUDPImageInfo(documentIds: string[], options?: { params: HttpParams }) {
    let url = this.endpointConfig.riaApi.webData.udp.imageInfo.getUrl(documentIds)
    return this.http.get<UdpImageResponse>(url, options)
  }

  // ------------------------
  // Client section
  // ------------------------
  public createDocumentIdSignedUrlMap(datasets: (TemplateDataset | Dataset | ImageRequest)[], resizeOption: ResizeOption = {}): Observable<{ [key: string]: string }> {
    if (datasets.length == 0) {
      return of({})
    }

    // collectionPath毎にリクエストするdocumentIdのリストをマッピングする。
    // (e.g.) collectionPathIdsMap = {"fr-prd01-dlk-um-runway/FRG/images": ["XXXXX", "XXXX"], "fr-prd01-dlk-um-runway/FRJP/images": ["XXXXX", "XXXX"]}
    let collectionPathIdsMap: { [key: string]: string[] } = {}
    for (let dataset of datasets) {
      let collectionPath = CommonUtils.getUDPImageCollectionPath(dataset.src)
      let documentId = dataset.src.split('?')[0].split('/').slice(-1)[0]
      if (!(collectionPath in collectionPathIdsMap)) {
        collectionPathIdsMap[collectionPath] = []
      }
      collectionPathIdsMap[collectionPath].push(documentId)
    }
    // collectionPath毎にリクエストするdocumentIdのリストをchunkに分ける。
    // (e.g.) collectionPathIdsChunkMap = {"fr-prd01-dlk-um-runway/FRG/images": [["XXXXX", "XXXX"], ["YYYY", "YYYY"]], "fr-prd01-dlk-um-runway/FRJP/images": [["XXXXX", "XXXX"], ["YYYY", "YYYY"]]}
    let collectionPathIdsChunkMap: { [key: string]: string[][] } = {}
    let chunkSize = this.MAX_CHUNK_SIZE
    for (let [_collectionPath, _documentIds] of Object.entries(collectionPathIdsMap)) {
      collectionPathIdsChunkMap[_collectionPath] = _documentIds.reduce((prevVal, _, i) => i % chunkSize ? prevVal : [...prevVal, _documentIds.slice(i, i + chunkSize)], [])
    }
    console.info('collectionPath and documentIdsChunk Mapping:', collectionPathIdsChunkMap)

    let requestSources: ObservableInput<UdpImageResponse>[] = []
    for (let [_collectionPath, _documentIdsChunkList] of Object.entries(collectionPathIdsChunkMap)) {
      let params = { params: new HttpParams().set('collectionPath', _collectionPath) }
      for (let _documentIdsChunk of _documentIdsChunkList) {
        requestSources.push(this.getUDPImageInfo(_documentIdsChunk, params))
      }
    }

    return forkJoin(requestSources)
      .pipe(
        map((udpImageResList) => {
          // documentIdとsignedUrlのマッピングを作成する。
          // (e.g.) documentIdSignedUrlMap = {"XXXX": "https://storage.googleapis.com/fr-prd01-tky-dlk-up-runway/FRG/...", "YYYY": "https://..."}
          let documentIdSignedUrlMap: { [key: string]: string } = {}
          udpImageResList.forEach((udpImageRes) => {
            udpImageRes.signedUrls.forEach((signedUrlObj) => {
              documentIdSignedUrlMap[signedUrlObj.imageId] = this.endpointConfig.riaApi.webData.udp.signedImage.getUrl(signedUrlObj.signedUrl, resizeOption.width, resizeOption.height)
            })
          })
          return documentIdSignedUrlMap
        })
      )
  }

  public loadImage(targetObject: any, targetProperty: string, src: string, size: string = "XS", resizeOption: ResizeOption = {}) {
    let documentId = src.split('?')[0].split('/').slice(-1)[0]
    let params = new HttpParams().set('collectionPath', this.getUDPImageCollectionPath(src)).set('size', size)
    const maxRetryCount = 3
    let retryCount = 0

    const loadSignedUrl = () => {
      this.getUDPImageInfo([documentId], { params: params })
        .subscribe((udpImageResponse) => {
          let signedUrlData = udpImageResponse.signedUrls[0]
          targetObject[targetProperty] = this.endpointConfig.riaApi.webData.udp.signedImage.getUrl(signedUrlData.signedUrl, resizeOption.width, resizeOption.height)
        },
          (err: HttpErrorResponse) => {
            retryCount++;
            this.logger.warn(`Retry get signed url for ${documentId}. Retry count: ${retryCount}/${maxRetryCount}`)
            if (retryCount < maxRetryCount) {
              // 1秒待ってから画像再取得する。
              setTimeout(this.loadImage, 1000)
            }
            else {
              this.logger.warn(`Failed to retry image acquisition for ${documentId}. Retry count: ${retryCount}/${maxRetryCount}`)
            }
          });
    }

    loadSignedUrl();
  }
}
